import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
  useLayoutEffect,
} from "react";

export const PARTNER_PORTAL_NEW_BRANDING_FEATURE_FLAG =
  "CRE-1844-partner-portal-new-branding";

interface ClientSideLDProviderProps {
  clientSideID: string | null | undefined;
  children: React.ReactNode;
}

interface LDContextValue {
  flags: Record<string, any>;
  ldClient: any;
  error: Error | null;
  isReady: boolean;
}

const LDContext = createContext<LDContextValue>({
  flags: {},
  ldClient: null,
  error: null,
  isReady: false,
});

export const useLaunchDarklyFlags = () => useContext(LDContext);

declare global {
  interface Window {
    LDClient?: any;
    ldScriptLoaded?: boolean;
  }
}

/**
 * Wrapper for LaunchDarkly that only initializes on the client side.
 * Uses the CDN-loaded LaunchDarkly SDK to avoid webpack compatibility issues.
 */
export const ClientSideLDProvider: React.FC<ClientSideLDProviderProps> = ({
  clientSideID,
  children,
}) => {
  const [flags, setFlags] = useState<Record<string, any>>({});
  const [ldClient, setLdClient] = useState<any>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isClient, setIsClient] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) {
      return;
    }

    // If no client ID configured, skip LaunchDarkly entirely
    if (!clientSideID) {
      console.log(
        "[LaunchDarkly] No client ID configured, using default flags",
      );
      // Defer state updates to prevent hydration timing issues
      setTimeout(() => {
        setFlags({});
        setIsReady(true);
      }, 0);
      return;
    }

    // Wait for LaunchDarkly to be loaded from CDN
    let cleanup: (() => void) | undefined;

    const initializeLaunchDarkly = () => {
      if (typeof window !== "undefined" && window.LDClient) {
        try {
          // LaunchDarkly SDK v3 requires 'kind' property in context
          const initialContext = {
            kind: "user",
            anonymous: true,
          };
          const client = window.LDClient.initialize(
            clientSideID,
            initialContext,
          );

          client.on("ready", () => {
            const allFlags = client.allFlags();
            setFlags(allFlags);
            setLdClient(client);
            setIsReady(true);
          });

          client.on("change", () => {
            const allFlags = client.allFlags();
            setFlags(allFlags);
          });

          // Store cleanup function to be called on unmount
          cleanup = () => {
            client.close();
          };
        } catch (err) {
          const caughtError =
            err instanceof Error ? err : new Error(String(err));
          console.error("[LaunchDarkly] Failed to initialize:", caughtError);
          setError(caughtError);
          setIsReady(true);
        }
      }
    };

    // Check if script already loaded (handles race condition where script loads before provider mounts)
    if (window.ldScriptLoaded || window.LDClient) {
      initializeLaunchDarkly();
    } else {
      // Listen for script load event
      const handleLDReady = () => {
        initializeLaunchDarkly();
      };

      window.addEventListener("ldready", handleLDReady, { once: true });

      // Fallback timeout in case script fails to load from CDN
      const fallbackTimeout = setTimeout(() => {
        window.removeEventListener("ldready", handleLDReady);
        const timeoutError = new Error(
          "LaunchDarkly SDK failed to load from CDN after 2000ms",
        );
        console.error("[LaunchDarkly]", timeoutError.message);
        console.warn(
          "[LaunchDarkly] Continuing without feature flags - all flags will return default values",
        );
        setError(timeoutError);
        setFlags({});
        setIsReady(true);
      }, 2000);

      // Store cleanup for event listener and timeout
      cleanup = () => {
        window.removeEventListener("ldready", handleLDReady);
        clearTimeout(fallbackTimeout);
      };
    }

    // Return cleanup function to properly close client on unmount or clientSideID change
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [isClient, clientSideID]);

  // Apply app branding styles depending on flag value. Using useLayoutEffect to avoid flash of legacy styles
  useLayoutEffect(() => {
    if (typeof document !== "undefined" && isReady) {
      document.documentElement.setAttribute(
        "data-brand",
        flags?.[PARTNER_PORTAL_NEW_BRANDING_FEATURE_FLAG] ? "new" : "legacy",
      );
    }
  }, [isReady, flags]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(
    () => ({ flags, ldClient, error, isReady }),
    [flags, ldClient, error, isReady],
  );

  return <LDContext.Provider value={value}>{children}</LDContext.Provider>;
};
